import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../services/featureFlagService';
import { FEATURE_FLAG } from '../../constants/general';
import { TEXTS } from '../../flows/goToApp/constants';
import { IInvoiceSettingsRes, useInvoiceSettingsQuery } from '../../hooks/query/useInvoiceSettingsQuery';
import { useStore } from '../../store';
import { getActiveInvoicePage, validateInitialStep } from './utils';
import { InvoicesContext } from './contexts/InvoicesContext';
import { InvoiceHub } from './InvoiceHub/InvoiceHub';
import { InvoiceOnboarding } from './InvoiceOnboarding/InvoiceOnboarding';
import ComingSoon from '../../flows/goToApp';
import Spinner, { SpinnerColor } from '../../generic-components/Spinner';
import { COMPONENT_MAP_NAMES } from './consts';
import { ProfilePlanPromotion } from '../../flows/profile/components/ProfilePlanPromotion';
import { trackMixPanel } from '../../services/mixpanel.service';
import { planReportName } from '../../flows/planUpgrade/service';
import PageLayout from '../../generic-components/PageLayout/PageLayout';

export const Invoices = () => {
  const {
    user: { customerId },
  } = useStore((state) => state);
  const {
    customer: { customerProfile },
  } = useStore((state) => state.getSummaryData());
  const {
    actionFlags: { createInvoice },
  } = useStore((state) => state.getSummaryData());
  const resetAll = useStore((state) => state.resetAll);
  const [activeInvoicePageName, setActiveInvoicePageName] = useState<string>(COMPONENT_MAP_NAMES.SPINNER);
  const [externalInitialStep, setExternalInitialStep] = useState<string>('');
  const [settings, setSettings] = useState<IInvoiceSettingsRes | null>(null);
  const { initialStep } = useParams();
  const profileType = useStore((state) => state.summary.customer.customerProfile);
  const navigate = useNavigate();
  const location = useLocation();
  const shouldShowUpgrade = !validateFeatureFlag(FEATURE_FLAG.INVOICE) && canUpgradeToUseFeature(FEATURE_FLAG.INVOICE);

  const COMPONENT_MAP = {
    HUB: <InvoiceHub />,
    ONBOARDING: <InvoiceOnboarding />,
    COMING_SOON: <ComingSoon customTitle={TEXTS.TEXT_1_INVOICE} customSubTitle={TEXTS.TEXT_2_INVOICE} />,
    SPINNER: (
      <Spinner
        spinnerColor={SpinnerColor.Blue}
        height='2rem'
        width='2rem'
        containerClassName='tab-landscape:pl-[88px] pc-sm:pl-[296px]'
      />
    ),
    UPGRADE: <ProfilePlanPromotion shouldImmediatelyInvoke planType={profileType} />,
  };

  const { refetch: fetchInvoiceSettingsData } = useInvoiceSettingsQuery({
    customerId,
    onSuccess: (invoiceSettingsDataResponse: IInvoiceSettingsRes) => {
      setSettings(invoiceSettingsDataResponse);
      if (externalInitialStep || !createInvoice) {
        setActiveInvoicePageName(COMPONENT_MAP_NAMES.ONBOARDING);
      } else if (shouldShowUpgrade) {
        setActiveInvoicePageName(COMPONENT_MAP_NAMES.ONBOARDING);
      } else {
        setActiveInvoicePageName(getActiveInvoicePage(invoiceSettingsDataResponse?.finishCustomization, createInvoice));
        const searchParams = new URLSearchParams(window.location.search);
        const queryString = searchParams.toString();
        if (queryString) navigate(`/invoices?${queryString}`);
        else navigate('/invoices');
      }
    },
  });

  const fetchInvoiceSettingsDataHandler = () => {
    setExternalInitialStep('');
    setActiveInvoicePageName(COMPONENT_MAP_NAMES.SPINNER);
    fetchInvoiceSettingsData();
  };

  const handleClearInitialStep = () => {
    setExternalInitialStep('');
    fetchInvoiceSettingsData();
  };

  const contextValue = useMemo(
    () => ({
      onFinishOnboarding: fetchInvoiceSettingsDataHandler,
      initialObStep: externalInitialStep,
      clearInitialStep: handleClearInitialStep,
      updateSettings: setSettings,
      settings,
    }),
    [externalInitialStep, settings],
  );

  const activeComponent = useMemo(
    () => COMPONENT_MAP[activeInvoicePageName as keyof typeof COMPONENT_MAP],
    [activeInvoicePageName],
  );

  useEffect(() => {
    if (!externalInitialStep && !initialStep) return;
    fetchInvoiceSettingsData();
  }, [externalInitialStep, initialStep]);

  useEffect(() => {
    if (location.state === null) {
      trackMixPanel('View Lili Plans Full Offer Screen', {
        Origin: 'Invoicing',
        'Current Plan': planReportName(customerProfile),
      });
    } else {
      trackMixPanel('View Lili Plans Full Offer Screen', {
        Origin: 'Invoices',
        'Current Plan': planReportName(customerProfile),
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (initialStep && validateInitialStep(initialStep)) {
      setExternalInitialStep(initialStep);
    }
    return () => {
      resetAll();
    };
  }, []);

  return (
    <PageLayout>
      <InvoicesContext.Provider value={contextValue}>{activeComponent}</InvoicesContext.Provider>
    </PageLayout>
  );
};
